import { get } from "lodash-es";
import {
	default as React,
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import { useComplexState } from "use-complex-state";
import {
	getDataUser,
	getUserPermissionsFromAPI,
} from "../configuration/pages/users/repositories/UsersRepository";
import { useMesfRealtime } from "../hooks/useMesfRealtime";
import { UserReducer } from "../reducers/UserReducer";

const UserContext = createContext({
	state: UserReducer.getInitialState(),
	actions: UserReducer.actions,
	isLoading: true,
});

export const useUserContext = () => useContext(UserContext);

export const useHasPermission = () => {
	const { state } = useUserContext();

	return (permission: string) => {
		return state.permissions.some((p) => p === permission);
	};
};

export const useHasProfile = () => {
	const { state } = useUserContext();

	return (profile: string) => {
		return state.profiles.some((p) => p === profile);
	};
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const [state, actions] = useComplexState({
		initialState: UserReducer.getInitialState(),
		reducers: UserReducer.caseReducers,
	});
	const [permissionsLoaded, setPermissionsLoaded] = useState(false);
	const [profilesLoaded, setProfilesLoaded] = useState(false);

	const getUserId = () => {
		const sessionData = localStorage.getItem("userMESData") || "{}";
		const userData = JSON.parse(sessionData);
		return get(userData, "id", null);
	};

	const getUserPermissions = async () => {
		let userId = getUserId();
		if (userId) {
			try {
				const resp = (await getUserPermissionsFromAPI({
					UserId: userId,
				})) as {
					UserId: number;
					UserName: string;
					ProfilesId: string;
					Permissions: string;
					Profiles: string;
				};

				if (resp && resp.Permissions) {
					let permissionsArray = resp.Permissions.split(",");
					actions.setPermissions(permissionsArray);
					setPermissionsLoaded(true);
				}

				if (resp && resp.Profiles) {
					let profilesArray = resp.Profiles.split(",");
					actions.setProfiles(profilesArray);
					setProfilesLoaded(true);
				}

				// Load user's DefaultAssetId
				const userDataResp = await getDataUser({ userId });
				if (userDataResp && userDataResp.length > 0) {
					const userData = userDataResp[0];
					if (typeof userData.DefaultAssetId === "number") {
						actions.setDefaultAreaId(userData.DefaultAssetId);
					}
					actions.setUserId(userId);
				}
			} catch (error) {
				console.error("Failed to load permissions:", error);
			}
		}
	};

	let userId = getUserId();

	useEffect(() => {
		getUserPermissions();
	}, [userId]);

	useMesfRealtime({
		topics: ["SEC.GetPermissions"],
		onMessage: () => getUserPermissions(),
	});

	return (
		<UserContext.Provider
			value={{
				state,
				actions,
				isLoading: !permissionsLoaded || !profilesLoaded,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};
